const safeJsonParse = (value) => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const normalizeNavigation = (navigation) => {
  if (!navigation) return null;
  if (typeof navigation === 'string') return safeJsonParse(navigation);
  if (typeof navigation === 'object') return navigation;
  return null;
};

const normalizeParams = (params) => {
  if (!params) return {};
  if (typeof params === 'string') return safeJsonParse(params) || {};
  if (typeof params === 'object') return params;
  return {};
};

const pickFirstNonEmpty = (...values) => {
  for (const v of values) {
    if (v === 0) return '0';
    if (v === false) continue;
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
};

const getPostId = (notification, params) =>
  pickFirstNonEmpty(
    params?.postId,
    notification?.target?.postId,
    notification?.target?.id,
    notification?.metadata?.postId,
    notification?.postId
  );

const getCourseIdForCourseDetail = (notification, params) =>
  pickFirstNonEmpty(
    params?.courseId,
    params?.id,
    notification?.target?.courseId,
    notification?.metadata?.courseId,
    notification?.target?.type === 'course' ? notification?.target?.id : ''
  );

const getCourseIdForLessonDetail = (notification, params) =>
  pickFirstNonEmpty(
    params?.courseId,
    notification?.target?.courseId,
    notification?.metadata?.courseId
  );

const getLessonId = (notification, params) =>
  pickFirstNonEmpty(
    params?.lessonId,
    params?.id,
    notification?.target?.lessonId,
    notification?.metadata?.lessonId,
    notification?.target?.type === 'lesson' ? notification?.target?.id : ''
  );

const getFriendId = (notification, params) =>
  pickFirstNonEmpty(
    params?.friendId,
    notification?.target?.friendId,
    notification?.metadata?.friendId
  );

const getActorUserId = (notification) =>
  pickFirstNonEmpty(
    notification?.actor?.userId,
    notification?.actor?.id,
    notification?.writer?.id,
    notification?.writerId
  );

export const resolveNotificationUrl = (notification) => {
  if (!notification) return null;

  const navigation = normalizeNavigation(notification.navigation);
  const routeName = navigation?.routeName ? String(navigation.routeName) : '';
  const params = normalizeParams(navigation?.params);
  const type = notification?.type ? String(notification.type) : '';

  if (routeName) {
    if (routeName === 'PostDetail') {
      const postId = getPostId(notification, params);
      return postId ? `/post/${encodeURIComponent(postId)}` : null;
    }

    if (routeName === 'CourseDetail') {
      const courseId = getCourseIdForCourseDetail(notification, params);
      return courseId ? `/course/${encodeURIComponent(courseId)}` : null;
    }

    if (routeName === 'LessonDetail') {
      const courseId = getCourseIdForLessonDetail(notification, params);
      const lessonId = getLessonId(notification, params);
      if (courseId && lessonId) {
        return `/course/${encodeURIComponent(courseId)}/lesson/${encodeURIComponent(lessonId)}`;
      }
      if (courseId) return `/course/${encodeURIComponent(courseId)}`;
      return null;
    }

    if (routeName === 'ChatDetail') {
      const friendId = getFriendId(notification, params);
      return friendId ? `/chat?with=${encodeURIComponent(friendId)}` : '/chat';
    }

    if (routeName === 'UserProfile') {
      const userId = pickFirstNonEmpty(params?.userId);
      return userId ? `/profile/${encodeURIComponent(userId)}` : null;
    }

    if (routeName === 'FriendRequests') {
      const actorUserId = getActorUserId(notification);
      if (actorUserId) return `/profile/${encodeURIComponent(actorUserId)}`;
      return '/notifications';
    }

    if (routeName === 'PurchasedCourses') {
      return '/my-learning?tab=purchased';
    }

    if (routeName === 'AdminEnrollCourse') {
      return null;
    }
  }

  if (type) {
    if (
      type === 'post.like' ||
      type === 'post.comment' ||
      type === 'comment.created' ||
      type === 'comment.reply'
    ) {
      const postId = getPostId(notification, params);
      return postId ? `/post/${encodeURIComponent(postId)}` : null;
    }

    if (type === 'comment.like') {
      const courseId = getCourseIdForLessonDetail(notification, params);
      const lessonId = pickFirstNonEmpty(notification?.target?.lessonId, notification?.metadata?.lessonId);
      if (courseId && lessonId) {
        return `/course/${encodeURIComponent(courseId)}/lesson/${encodeURIComponent(lessonId)}`;
      }

      const postId = getPostId(notification, params);
      return postId ? `/post/${encodeURIComponent(postId)}` : null;
    }

    if (type === 'lesson.added' || type === 'lesson.comment' || type === 'lesson.comment_reply') {
      const courseId = getCourseIdForLessonDetail(notification, params);
      const lessonId = getLessonId(notification, params);
      if (courseId && lessonId) {
        return `/course/${encodeURIComponent(courseId)}/lesson/${encodeURIComponent(lessonId)}`;
      }
      if (courseId) return `/course/${encodeURIComponent(courseId)}`;
      return null;
    }

    if (type === 'chat.message') {
      const friendId = getFriendId(notification, params);
      return friendId ? `/chat?with=${encodeURIComponent(friendId)}` : '/chat';
    }

    if (type === 'payment.activated') {
      return '/my-learning?tab=purchased';
    }

    if (type === 'payment.created' || type === 'chat.support') {
      return null;
    }

    if (type === 'friend.request' || type === 'friend.accept') {
      const actorUserId = getActorUserId(notification);
      return actorUserId ? `/profile/${encodeURIComponent(actorUserId)}` : null;
    }
  }

  const targetType = notification?.target?.type ? String(notification.target.type) : '';
  const targetId = pickFirstNonEmpty(notification?.target?.id);

  if (targetType === 'post' && targetId) return `/post/${encodeURIComponent(targetId)}`;
  if (targetType === 'course' && targetId) return `/course/${encodeURIComponent(targetId)}`;
  if (targetType === 'lesson' && targetId) {
    const courseId = pickFirstNonEmpty(notification?.target?.courseId, notification?.metadata?.courseId);
    if (courseId) {
      return `/course/${encodeURIComponent(courseId)}/lesson/${encodeURIComponent(targetId)}`;
    }
  }

  return null;
};
